import React, { useCallback, useState, useMemo } from 'react';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import useAPI from 'services/useAPI';
// import createQueryRequest from 'services/createQueryRequest';
import createMutationRequest from 'utils/createMutationRequest';

import { useTranslation } from 'localization/useTranslation';
import SimpleTable, { createDefaultActions } from 'components/Common/SimpleTable/SimpleTable';
import _ from 'lodash';
import T from 'localization/LocalizationKeys';
import { useHistory } from 'react-router-dom';
import { notificationsKeys, useNotifications } from './queries';
import ContentPage from 'components/ContentView/ContentPage/ContentPage';
import useAlerts from 'utils/hooks/useAlerts';
import ConfirmDeleteModal from 'components/Modals/ConfirmDeleteModal';


const columns = [
  {
    label: T.common.displayname,
    path: 'displayName',
    searchItem: true,
    isSortable: true
  },
  {
    label: T.common.description,
    path: 'description',
    searchItem: true,
    isSortable: false
  }
];


const AdminNotificationsPage = ({ breadcrumbs }) => {
  const api = useAPI();
  const history = useHistory();
  const queryClient = useQueryClient();
  const T = useTranslation();
  const [alerts, addSuccessAlert, addErrorAlert] = useAlerts();
  const [confirmDeleteItem, setConfirmDeleteItem] = useState(null);

  const { notifications, notificationsQuery } = useNotifications();

  const navigateToNotification = useCallback((notificationId) => {
    history.push(`/admin/notifications/${notificationId}`);
  }, [history]);

  const onRowClick = useCallback((row) => {
    navigateToNotification(row.id);
  }, [navigateToNotification]);

  const onDelete = useCallback((notification) => setConfirmDeleteItem(notification), []);
  const cancelDelete = useCallback(() => setConfirmDeleteItem(null), []);
  const actions = useMemo(() => createDefaultActions({ onDelete }), [onDelete]);
  const deleteNotification = useMutation({
    mutationFn: createMutationRequest(api.core.deleteNotification),
    onMutate: (deleteItem) => {
      const oldData = [...queryClient.getQueryData(notificationsKeys.notifications())];
      queryClient.setQueryData(notificationsKeys.notifications(), old => {
        return _.reject(old, { id: deleteItem.id });
      });
      return { deleteItem, oldData };
    },
    onSuccess: () => {
      addSuccessAlert(T.common.request.delete.success);
      setConfirmDeleteItem(null);
      notificationsQuery.refetch({ throwOnError: true });
    },
    onError: (unusedError, unusedVariables, context) => {
      addErrorAlert(T.common.request.delete.failed);
      queryClient.setQueryData(notificationsKeys.notifications(), context?.oldData);
    }
  });

  return (
    <ContentPage breadcrumb={breadcrumbs}>
      { alerts }
      <SimpleTable
        isLoading={notificationsQuery.isLoading}
        onRowClick={onRowClick}
        globalActions={[{ onClick: () => navigateToNotification('new-notification') }]}
        data={notifications}
        columns={columns}
        actions={actions}
      />
      <ConfirmDeleteModal
        isLoading={deleteNotification.isLoading}
        onConfirm={item => deleteNotification.mutate(item.id)}
        item={confirmDeleteItem}
        itemName={confirmDeleteItem?.displayName ?? T.common.noname}
        onCancel={cancelDelete}
      />
    </ContentPage>
  );
};

export default AdminNotificationsPage;
